import type { ResultOf } from '@graphql-typed-document-node/core'
import DataLoader from 'dataloader'
import { atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query'
import { atomFamily } from 'jotai/utils'
import isEqual from 'react-fast-compare'
import { viewer$, viewerState$ } from '~/features/viewer/query'
import { graphql } from '~/lib/graphql'
import type { PlanId, RestaurantId } from '~/lib/graphql/scalars'
import { callBatchGraphql, callGraphql } from '~/stores/jotaiQueryHelper'

type Key = { restaurantId: RestaurantId; planId: PlanId }

// #region query

// api/v2/graphql のために export
export const FavoritePlanQuery = graphql(/* GraphQL */ `
  query FavoritePlanQuery($restaurantId: RestaurantId!, $planId: PlanId!) {
    restaurant(restaurantId: $restaurantId) {
      plan(planId: $planId) {
        viewerFavorited
      }
    }
  }
`)

async function fetchIsFavoritePlans(variables: readonly Key[]) {
  const res = await callBatchGraphql(
    FavoritePlanQuery,
    // readonly をはがす
    variables as Key[]
  )
  return res.map((r) => {
    if ('errors' in r) {
      // FIXME: もうちょっとなんとかしたい
      return new Error(r.errors.join(', '))
    }
    return r.data.restaurant?.plan?.viewerFavorited ?? false
  })
}

const isFavoritePlanDataLoader = new DataLoader<Key, boolean>(fetchIsFavoritePlans, {
  batchScheduleFn: (cb) => setTimeout(cb, 16),
  // キャッシュは TanStack Query に任せる
  cache: false,
})

/** key のプランがお気に入りに登録しているかを取得 */
export const isFavoritePlan$ = atomFamily(
  (key: Key) =>
    atomWithQuery((get) => {
      const viewerState = get(viewerState$)
      return {
        queryKey: ['isFavoritePlan', key.restaurantId, key.planId],
        queryFn: () => isFavoritePlanDataLoader.load(key),
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
        enabled: !viewerState.isLoading && viewerState.state === 'member',
      }
    }),
  isEqual
)

// #endregion

// #region mutation

// api/v2/graphql のために export
export const FavoritePlanMutation = graphql(/* GraphQL */ `
  mutation FavoritePlanMutation($planId: PlanId!) {
    favoritePlan(planId: $planId)
  }
`)

// api/v2/graphql のために export
export const UnfavoritePlanMutation = graphql(/* GraphQL */ `
  mutation UnfavoritePlanMutation($planId: PlanId!) {
    unfavoritePlan(planId: $planId)
  }
`)

export type FavoritePlanRequest = {
  verb: 'favorite' | 'unfavorite'
  planId: PlanId
  restaurantId: RestaurantId
}

type FavoritePlanErrorTypes = NonNullable<
  | ResultOf<typeof FavoritePlanMutation>['favoritePlan']
  | ResultOf<typeof UnfavoritePlanMutation>['unfavoritePlan']
>

class FavoritePlanError extends Error {
  errorType: FavoritePlanErrorTypes

  constructor(verb: FavoritePlanRequest['verb'], errorType: FavoritePlanErrorTypes) {
    super(`failed to ${verb} plan`)
    this.errorType = errorType
  }
}

export const favoritePlan$ = atomWithMutation<
  null,
  FavoritePlanRequest,
  Error,
  { prev: boolean | undefined }
>((get) => {
  const viewerState = get(viewerState$)
  return {
    enabled: !viewerState.isLoading && viewerState.state === 'member',
    mutationFn: async ({ verb, planId }) => {
      // TODO: debounce
      switch (verb) {
        case 'favorite': {
          const res = await callGraphql(FavoritePlanMutation, { planId })
          if (res.favoritePlan !== null) {
            throw new FavoritePlanError(verb, res.favoritePlan)
          }
          return null
        }
        case 'unfavorite': {
          const res = await callGraphql(UnfavoritePlanMutation, { planId })
          if (res.unfavoritePlan !== null) {
            throw new FavoritePlanError(verb, res.unfavoritePlan)
          }
          return null
        }
        default:
          throw new Error(`unknown verb ${verb}`)
      }
    },
    onMutate: ({ verb, planId, restaurantId }) => {
      const ctx: { prev: boolean | undefined } = { prev: undefined }
      // optimistic update
      get(queryClientAtom).setQueryData(
        ['isFavoritePlan', restaurantId, planId],
        (prev: boolean) => {
          ctx.prev = prev
          return verb === 'favorite'
        }
      )
      return ctx
    },
    // XXX: レプリ遅延のため取得しなおしは止める
    // TODO: mutation の返り値で最新の値を取れるようにする
    //onSettled: (_data, _err, { planId, restaurantId }) => {
    //  get(isFavoritePlan$({ planId, restaurantId })).refetch()
    //},
    onError: (err, { planId }, context) => {
      // rollback
      get(queryClientAtom).setQueryData(['isFavoriteRestaurant', planId], context?.prev)
      if (err instanceof FavoritePlanError) {
        if (err.errorType === 'LOGIN_REQUIRED') {
          // ログアウトしているので viewer$ を再取得
          get(viewer$).refetch()
        }
        // TODO: toast だす？
      }
    },
  }
})

// #endregion
